import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { userService, searchService } from "../../services/api";
import { flagImageUrl } from "../../components/agent-tender/FlagImg";
import CoachDetailsSection from "../../components/profile/sections/CoachDetailsSection";
import ExperienceSection from "../../components/profile/career-timeline/ExperienceSection";
import AchievementsSection from "../../components/profile/career-timeline/AchievementsSection";
import CoachLicensesLanguagesSection from "../../components/profile/sections/CoachLicensesLanguagesSection";
import PlayingStyleSection from "../../components/profile/sections/PlayingStyleSection";
import DefensiveTacticsSection from "../../components/profile/sections/DefensiveTacticsSection";
import OffensiveTacticsSection from "../../components/profile/sections/OffensiveTacticsSection";
import CoachingPreferencesNote from "../../components/profile/sections/CoachingPreferencesNote";
import CoachingPhilosophySection from "../../components/profile/sections/CoachingPhilosophySection";
import CoachingPersonalitySection from "../../components/profile/coaching-personality/CoachingPersonalitySection";
import AddLicenseModal from "../../components/profile/modals/AddLicenseModal";
import AddLanguageModal from "../../components/profile/modals/AddLanguageModal";
import { COACH_LICENSE_OPTIONS } from "../../constants/coachLicenses";

const CoachProfile = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const navigate = useNavigate();

  // Profile editing state
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  // Per-field save errors (e.g. an implausible date of birth) so the message
  // lands on the field that caused it, not only in the banner.
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  // Form data state
  const [formData, setFormData] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);

  // Country options for the nationality picker. Canonical list from the server
  // (GET /dashboard/countries/) — no local country table. The stored value is
  // the alpha-3 code, which is the shape the sports feed also carries; the flag
  // comes from the platform's image-flag resolver, never an emoji.
  const [countryOptions, setCountryOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const response = await searchService.getCountries();
      if (cancelled || !response.success) return;
      setCountryOptions(
        (response.data || [])
          .filter((c) => c?.alpha3code && c?.name)
          .map((c) => ({
            value: String(c.alpha3code).toUpperCase(),
            label: c.name,
            image: flagImageUrl(c),
          }))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch coach profile data
  useEffect(() => {
    const fetchCoachProfile = async () => {
      setLoading(true);
      try {
        const response = await userService.getCoachProfile();
        if (response.success) {
          const coachData = response.data;
          
          // Transform API data to form data structure
          const transformedData = {
            account_id: coachData.account_id,
            ts_coach_id: coachData.ts_coach_id || '',
            firstName: coachData.name?.split(' ')[0] || '',
            lastName: coachData.name?.split(' ').slice(1).join(' ') || '',
            email: coachData.email || '',
            mobile: coachData.phone_number || '',
            profileImage: coachData.profile_picture_url || '',
            // Resolved values (self-entry first, else the sports data, else empty)
            position: coachData.position || '',
            nationality: coachData.nationality || '',
            nationalityLogo: coachData.nationality_logo || '',
            // The coach's OWN entries — all optional, all editable here.
            dateOfBirth: coachData.date_of_birth || '',
            selfPosition: coachData.self_position || '',
            selfNationality: coachData.self_nationality || '',
            resumeUrl: coachData.resume_url || '',
            clubDetails: coachData.club_details || null,
            specialization: coachData.specialization || '',
            playingStyles: coachData.playing_styles || [],
            defensiveTactics: coachData.defensive_tactics || [],
            offensiveTactics: coachData.offensive_tactics || [],
            additionalNotes: coachData.additional_notes || '',
            experiences: (coachData.experiences || []).map(exp => ({
              id: exp.id || Date.now() + Math.random(),
              club: exp.club_name || '',
              role: exp.position || '',
              position: exp.position || '',
              country: exp.country || '',
              from: exp.start_year && exp.start_month 
                ? `${exp.start_year}-${String(exp.start_month).padStart(2, '0')}` 
                : '',
              to: exp.end_year && exp.end_month 
                ? `${exp.end_year}-${String(exp.end_month).padStart(2, '0')}` 
                : (exp.end_year === null || exp.end_month === null) ? 'present' : '',
              startDate: exp.start_year && exp.start_month 
                ? `${exp.start_year}-${String(exp.start_month).padStart(2, '0')}` 
                : '',
              endDate: exp.end_year && exp.end_month 
                ? `${exp.end_year}-${String(exp.end_month).padStart(2, '0')}` 
                : (exp.end_year === null || exp.end_month === null) ? 'present' : '',
            })),
            trophies: (coachData.trophies || []).map(trophy => ({
              id: trophy.id || Date.now() + Math.random(),
              title: trophy.title || '',
              club: trophy.club || '',
              year: trophy.year || trophy.season || '',
              season: trophy.season || trophy.year || '',
            })),
            licenses: (coachData.licenses || []).map(license => ({
              id: license.id,
              type: license.license_type || '',
              licenseId: license.license_id || '',
              issueDate: license.issue_date || '',
              year: license.year?.toString() || '',
              document: license.document_url || null,
            })),
            languages: (coachData.languages || []).map(lang => ({
              id: lang.id,
              language: lang.language || '',
              proficiency: lang.proficiency_level || '',
            })),
            education: [],
            trainings: [],
          };
          
          setFormData(transformedData);
          setInitialFormData(JSON.parse(JSON.stringify(transformedData)));
        } else {
          throw new Error(response.error || "Failed to fetch coach profile");
        }
      } catch (error) {
        console.error("Error fetching coach profile:", error);
        setError(error.message || "Failed to load coach profile.");
      } finally {
        setLoading(false);
      }
    };

    fetchCoachProfile();
  }, []);

  // Handle edit/save/cancel actions
  const handleEdit = () => {
    setIsEditing(true);
    if (formData && !initialFormData) {
      setInitialFormData(JSON.parse(JSON.stringify(formData)));
    }
  };

  const handleCancel = () => {
    if (initialFormData) {
      setFormData(JSON.parse(JSON.stringify(initialFormData)));
    }
    setError(null);
    setFieldErrors({});
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setFieldErrors({});

    try {
      // Build update payload
      const updateData = new FormData();
      let hasFile = false;

      // Check for resume file changes
      if (formData.resumeFile && formData.resumeFile instanceof File) {
        updateData.append('resume', formData.resumeFile);
        hasFile = true;
      }

      // Helper function to check if arrays have changed
      const hasArrayChanged = (newArr, oldArr) => {
        return JSON.stringify(newArr || []) !== JSON.stringify(oldArr || []);
      };

      // Add JSON fields to FormData
      if (formData.specialization !== initialFormData?.specialization) {
        updateData.append('specialization', formData.specialization || '');
      }
      
      if (hasArrayChanged(formData.playingStyles, initialFormData?.playingStyles)) {
        // Append each playing style
        formData.playingStyles.forEach(style => {
          updateData.append('playing_styles', style);
        });
      }
      
      if (hasArrayChanged(formData.defensiveTactics, initialFormData?.defensiveTactics)) {
        // Append each defensive tactic
        formData.defensiveTactics.forEach(tactic => {
          updateData.append('defensive_tactics', tactic);
        });
      }

      if (hasArrayChanged(formData.offensiveTactics, initialFormData?.offensiveTactics)) {
        // Append each offensive tactic
        (formData.offensiveTactics || []).forEach(tactic => {
          updateData.append('offensive_tactics', tactic);
        });
      }

      if (formData.additionalNotes !== initialFormData?.additionalNotes) {
        updateData.append('additional_notes', formData.additionalNotes || '');
      }

      // Helper function to transform experience data for API
      const transformExperiences = (experiences) => {
        return (experiences || []).map(exp => {
          // Parse dates from format "YYYY-MM" or "YYYY-MM-DD"
          const parseDate = (dateStr) => {
            if (!dateStr || dateStr === 'present') return null;
            const parts = dateStr.split('-');
            if (parts.length >= 2) {
              return {
                month: parseInt(parts[1]) || 1,
                year: parseInt(parts[0]) || new Date().getFullYear()
              };
            }
            return null;
          };

          const startDate = parseDate(exp.from || exp.startDate);
          const endDate = exp.to === 'present' || !exp.to ? null : parseDate(exp.to || exp.endDate);

          return {
            club_name: exp.club || '',
            position: exp.role || exp.position || '',
            country: exp.country || '',
            start_month: startDate?.month || 1,
            start_year: startDate?.year || new Date().getFullYear(),
            end_month: endDate?.month || null,
            end_year: endDate?.year || null,
          };
        });
      };

      // Helper function to transform trophies data for API
      const transformTrophies = (trophies) => {
        return (trophies || []).map(trophy => ({
          title: trophy.title || '',
          club: trophy.club || '',
          year: trophy.year || trophy.season || new Date().getFullYear(),
        }));
      };

      // Helper function to transform licenses data for API
      const transformLicenses = (licenses) => {
        return (licenses || []).map(license => ({
          license_type: license.type || '',
          license_id: license.licenseId || '',
          issue_date: license.issueDate || '',
          year: parseInt(license.year) || new Date().getFullYear(),
        }));
      };

      // Helper function to transform languages data for API
      const transformLanguages = (languages) => {
        return (languages || []).map(lang => ({
          language: lang.language || '',
          proficiency_level: lang.proficiency || '',
        }));
      };

      // Build payload - always use JSON for complex data structures
      let payload = {};
      let config = {
        headers: {
          "Content-Type": "application/json",
        },
      };

      // Add specialization if changed
      if (formData.specialization !== initialFormData?.specialization) {
        payload.specialization = formData.specialization || '';
      }
      
      // Add playing styles if changed
      if (hasArrayChanged(formData.playingStyles, initialFormData?.playingStyles)) {
        payload.playing_styles = formData.playingStyles || [];
      }
      
      // Add defensive tactics if changed
      if (hasArrayChanged(formData.defensiveTactics, initialFormData?.defensiveTactics)) {
        payload.defensive_tactics = formData.defensiveTactics || [];
      }

      // Add offensive tactics if changed
      if (hasArrayChanged(formData.offensiveTactics, initialFormData?.offensiveTactics)) {
        payload.offensive_tactics = formData.offensiveTactics || [];
      }

      // Add additional notes if changed
      if (formData.additionalNotes !== initialFormData?.additionalNotes) {
        payload.additional_notes = formData.additionalNotes || '';
      }

      // Self-entered personal details. Empty is meaningful: it clears the entry
      // and hands the answer back to the sports data, so `null` is sent rather
      // than the field being dropped.
      if (formData.dateOfBirth !== initialFormData?.dateOfBirth) {
        payload.date_of_birth = formData.dateOfBirth || null;
      }
      if (formData.selfPosition !== initialFormData?.selfPosition) {
        payload.self_position = formData.selfPosition || null;
      }
      if (formData.selfNationality !== initialFormData?.selfNationality) {
        payload.self_nationality = formData.selfNationality || null;
      }

      // Add experiences if changed
      if (hasArrayChanged(formData.experiences, initialFormData?.experiences)) {
        payload.experiences = transformExperiences(formData.experiences);
      }

      // Add trophies if changed
      if (hasArrayChanged(formData.trophies, initialFormData?.trophies)) {
        payload.trophies = transformTrophies(formData.trophies);
      }

      // Add licenses if changed
      if (hasArrayChanged(formData.licenses, initialFormData?.licenses)) {
        payload.licenses = transformLicenses(formData.licenses);
      }

      // Add languages if changed
      if (hasArrayChanged(formData.languages, initialFormData?.languages)) {
        payload.languages = transformLanguages(formData.languages);
      }

      // If there's a file, use FormData
      if (hasFile) {
        const formDataPayload = new FormData();
        
        // Add file
        formDataPayload.append('resume', formData.resumeFile);
        
        // Add all other fields as JSON string or individual fields
        Object.keys(payload).forEach(key => {
          if (Array.isArray(payload[key])) {
            // For arrays, append each item or stringify
            if (key === 'experiences' || key === 'trophies' || key === 'licenses' || key === 'languages') {
              formDataPayload.append(key, JSON.stringify(payload[key]));
            } else {
              payload[key].forEach(item => {
                formDataPayload.append(key, item);
              });
            }
          } else {
            // A cleared optional field is `null`; FormData would stringify it
            // to "null", so send an empty value the API reads back as cleared.
            formDataPayload.append(key, payload[key] === null ? '' : payload[key]);
          }
        });
        
        payload = formDataPayload;
        config = {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        };
      }

      // Check if there are any changes
      if (!hasFile && Object.keys(payload).length === 0) {
        console.log("No changes detected, skipping API call");
        setIsEditing(false);
        setIsSaving(false);
        return;
      }

      const response = await userService.updateCoachProfile(payload, config);

      if (response.success) {
        // Update initial data to reflect saved state
        setInitialFormData(JSON.parse(JSON.stringify(formData)));
        setIsEditing(false);
        console.log("Coach profile updated successfully");
        
        // Refresh profile data
        const refreshResponse = await userService.getCoachProfile();
        if (refreshResponse.success) {
          const coachData = refreshResponse.data;
          const transformedData = {
            account_id: coachData.account_id,
            ts_coach_id: coachData.ts_coach_id || '',
            firstName: coachData.name?.split(' ')[0] || '',
            lastName: coachData.name?.split(' ').slice(1).join(' ') || '',
            email: coachData.email || '',
            mobile: coachData.phone_number || '',
            profileImage: coachData.profile_picture_url || '',
            // Resolved values (self-entry first, else the sports data, else empty)
            position: coachData.position || '',
            nationality: coachData.nationality || '',
            nationalityLogo: coachData.nationality_logo || '',
            // The coach's OWN entries — all optional, all editable here.
            dateOfBirth: coachData.date_of_birth || '',
            selfPosition: coachData.self_position || '',
            selfNationality: coachData.self_nationality || '',
            resumeUrl: coachData.resume_url || '',
            clubDetails: coachData.club_details || null,
            specialization: coachData.specialization || '',
            playingStyles: coachData.playing_styles || [],
            defensiveTactics: coachData.defensive_tactics || [],
            offensiveTactics: coachData.offensive_tactics || [],
            additionalNotes: coachData.additional_notes || '',
            experiences: (coachData.experiences || []).map(exp => ({
              id: exp.id || Date.now() + Math.random(),
              club: exp.club_name || '',
              role: exp.position || '',
              position: exp.position || '',
              country: exp.country || '',
              from: exp.start_year && exp.start_month 
                ? `${exp.start_year}-${String(exp.start_month).padStart(2, '0')}` 
                : '',
              to: exp.end_year && exp.end_month 
                ? `${exp.end_year}-${String(exp.end_month).padStart(2, '0')}` 
                : (exp.end_year === null || exp.end_month === null) ? 'present' : '',
              startDate: exp.start_year && exp.start_month 
                ? `${exp.start_year}-${String(exp.start_month).padStart(2, '0')}` 
                : '',
              endDate: exp.end_year && exp.end_month 
                ? `${exp.end_year}-${String(exp.end_month).padStart(2, '0')}` 
                : (exp.end_year === null || exp.end_month === null) ? 'present' : '',
            })),
            trophies: (coachData.trophies || []).map(trophy => ({
              id: trophy.id || Date.now() + Math.random(),
              title: trophy.title || '',
              club: trophy.club || '',
              year: trophy.year || trophy.season || '',
              season: trophy.season || trophy.year || '',
            })),
            licenses: (coachData.licenses || []).map(license => ({
              id: license.id,
              type: license.license_type || '',
              licenseId: license.license_id || '',
              issueDate: license.issue_date || '',
              year: license.year?.toString() || '',
              document: license.document_url || null,
            })),
            languages: (coachData.languages || []).map(lang => ({
              id: lang.id,
              language: lang.language || '',
              proficiency: lang.proficiency_level || '',
            })),
            education: [],
            trainings: [],
          };
          setFormData(transformedData);
          setInitialFormData(JSON.parse(JSON.stringify(transformedData)));
        }
      } else {
        if (response.fieldErrors) {
          setFieldErrors(response.fieldErrors);
        }
        throw new Error(response.error || "Failed to update coach profile");
      }
    } catch (error) {
      console.error("Error saving coach profile:", error);
      setError(error.message || "Failed to save coach profile. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // License and Language Options
  //
  // The licence list now lives in constants/coachLicenses.js. It is the COACH
  // list only: the UEFA ladder (Pro, A, B, C), the national FA licences and
  // the goalkeeping and fitness rungs. It used to be a copy of the staff
  // form's catalogue, which is how this page ended up offering Physiotherapist
  // and Wyscout while having no UEFA Pro at all. components/profile/
  // StaffProfileForm.js keeps its own inline copy, deliberately untouched.
  const licenseOptions = COACH_LICENSE_OPTIONS;

  const languageOptions = [
    { value: "english", label: "English" },
    { value: "german", label: "German" },
    { value: "french", label: "French" },
    { value: "spanish", label: "Spanish" },
    { value: "portuguese", label: "Portuguese" },
    { value: "arabic", label: "Arabic" },
    { value: "turkish", label: "Turkish" },
    { value: "russian", label: "Russian" },
    { value: "chinese", label: "Chinese" },
    { value: "japanese", label: "Japanese" },
    { value: "italian", label: "Italian" },
    { value: "dutch", label: "Dutch" },
    { value: "swedish", label: "Swedish" },
    { value: "norwegian", label: "Norwegian" },
    { value: "danish", label: "Danish" },
    { value: "finnish", label: "Finnish" },
    { value: "polish", label: "Polish" },
    { value: "czech", label: "Czech" },
    { value: "hungarian", label: "Hungarian" },
    { value: "other", label: "Other" },
  ];

  const proficiencyOptions = [
    { value: "native", label: "Native" },
    { value: "c2", label: "C2 (Proficient)" },
    { value: "c1", label: "C1 (Advanced)" },
    { value: "b2", label: "B2 (Upper Intermediate)" },
    { value: "b1", label: "B1 (Intermediate)" },
    { value: "a2", label: "A2 (Elementary)" },
    { value: "a1", label: "A1 (Beginner)" },
  ];

  // Handle add license
  const handleAddLicense = (licenseData) => {
    const newLicense = {
      id: Date.now(),
      type: licenseData.type,
      issueDate: licenseData.issueDate,
      licenseId: licenseData.licenseId,
      year: licenseData.year || '',
      document: licenseData.document,
    };
    handleInputChange('licenses', [...(formData.licenses || []), newLicense]);
    setShowLicenseModal(false);
  };

  // Handle add language
  const handleAddLanguage = (languageData) => {
    const newLanguage = {
      id: Date.now(),
      language: languageData.language,
      proficiency: languageData.proficiency,
    };
    handleInputChange('languages', [...(formData.languages || []), newLanguage]);
    setShowLanguageModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Profile Not Found</h1>
          <p className="text-gray-600">Unable to load coach profile.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Header */}
        <div className="bg-white py-4 border-b border-[#E5E6E8]">
          <div className="px-4 md:px-14 flex items-center justify-between">
            <h1 className="text-[20px] font-bold text-gray-900">My Profile</h1>
            <button 
              onClick={() => navigate(`/search/coach/${formData.ts_coach_id}`)} 
              className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg"
            >
              Preview public profile
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-4 md:px-14 py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg
                    className="h-5 w-5 text-red-400"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={() => setError(null)}
                      className="bg-red-100 px-2 py-1.5 rounded-md text-red-800 hover:bg-red-200 text-sm font-medium"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="bg-[#F8F9FB] rounded-lg shadow-sm px-4 md:px-14 py-6 space-y-8">
          {/* Coach Details Section */}
          <CoachDetailsSection
            isEditing={isEditing}
            isSaving={isSaving}
            onEdit={handleEdit}
            onCancel={handleCancel}
            onSaveClick={handleSave}
            formData={formData}
            handleInputChange={handleInputChange}
            countryOptions={countryOptions}
            dateOfBirthError={
              Array.isArray(fieldErrors.date_of_birth)
                ? fieldErrors.date_of_birth[0]
                : fieldErrors.date_of_birth
            }
            onDocumentUpload={async (type, filename, file) => {
              // Handle document upload if needed
              handleInputChange('resumeFile', file);
            }}
            isLoadingDocuments={false}
            onOpenMobileModal={() => {
              // Handle mobile modal
            }}
            onOpenImageUpload={() => {
              // Handle image upload
            }}
            onOpenChangePassword={() => {
              // Handle password change
            }}
          />

          {/* Experience Section */}
          <ExperienceSection
            isEditing={isEditing}
            experience={formData.experiences || []}
            onUpdateExperience={(newExperience) => {
              handleInputChange('experiences', newExperience);
            }}
            hideResumeUpload={true}
          />

          {/* Achievements/Trophies Section */}
          <AchievementsSection
            isEditing={isEditing}
            achievements={formData.trophies || []}
            onUpdateAchievements={(newAchievements) => {
              handleInputChange('trophies', newAchievements);
            }}
          />

          {/* Licenses & Languages Section */}
          <CoachLicensesLanguagesSection
            isEditing={isEditing}
            formData={formData}
            onAddLicense={() => setShowLicenseModal(true)}
            onAddLanguage={() => setShowLanguageModal(true)}
            onRemoveLanguage={(id) => {
              const updatedLanguages = (formData.languages || []).filter(lang => lang.id !== id);
              handleInputChange('languages', updatedLanguages);
            }}
            proficiencyOptions={proficiencyOptions}
            setFormData={setFormData}
          />

          {/* The three sections below are PREFERENCES, and the line says so
              before the first of them. A list of tactical terms next to a name
              reads like a record the platform stands behind; it is nothing of
              the sort. Nobody verifies these words, nothing filters on them,
              and none of it is passed on as data. */}
          <CoachingPreferencesNote wrapperClassName="bg-white px-6 py-4 border border-[#E6E6E6]" />

          {/* Playing Style Section */}
          <PlayingStyleSection
            isEditing={isEditing}
            playingStyles={formData.playingStyles || []}
            onUpdatePlayingStyles={(newStyles) => {
              handleInputChange('playingStyles', newStyles);
            }}
          />

          {/* Defensive Tactics Section */}
          <DefensiveTacticsSection
            isEditing={isEditing}
            defensiveTactics={formData.defensiveTactics || []}
            onUpdateDefensiveTactics={(newTactics) => {
              handleInputChange('defensiveTactics', newTactics);
            }}
          />

          {/* Offensive Tactics Section — the missing half of the pair, added
              after the defensive one so nothing a coach already knows moves. */}
          <OffensiveTacticsSection
            isEditing={isEditing}
            offensiveTactics={formData.offensiveTactics || []}
            onUpdateOffensiveTactics={(newTactics) => {
              handleInputChange('offensiveTactics', newTactics);
            }}
          />

          {/* Coaching Philosophy Section.
              This is `additional_notes` on the coach record — the same thing the
              staff profile calls the coaching philosophy. It REPLACES the former
              "ADDITIONAL NOTES" card rather than sitting next to it: one data
              field must never carry two input boxes, or one of them wins on save
              and the other's text disappears without a word. */}
          <CoachingPhilosophySection
            isEditing={isEditing}
            value={formData.additionalNotes || ''}
            onChange={(newValue) => {
              handleInputChange('additionalNotes', newValue);
            }}
          />

          {/* Coaching Personality.
              The staff profile already carried a slot for this ("PERSONALITY
              TEST . COMING SOON"); the coach profile never had one, so here it
              is an addition rather than a replacement. It sits after the
              philosophy, which is where the staff profile's slot sits too, so
              the two profile types read the same way top to bottom.
              It owns its own data and renders NOTHING at all when the section
              does not apply, so nothing above it is affected either way. */}
          <CoachingPersonalitySection />
        </div>
      </div>

      {/* License Modal */}
      {showLicenseModal && (
        <AddLicenseModal
          onClose={() => setShowLicenseModal(false)}
          onAdd={handleAddLicense}
          licenseOptions={licenseOptions}
        />
      )}

      {/* Language Modal */}
      {showLanguageModal && (
        <AddLanguageModal
          onClose={() => setShowLanguageModal(false)}
          onAdd={handleAddLanguage}
          languageOptions={languageOptions}
          proficiencyOptions={proficiencyOptions}
        />
      )}
    </div>
  );
};

export default CoachProfile;

